<template>
  <div class="summit-detail-page">
    <header class="top-bar">
      <button class="icon-btn" @click="goBack">
        <i class="el-icon-arrow-left"></i>
      </button>
      <h2>总结详情</h2>
      <div class="header-placeholder"></div>
    </header>

    <div v-if="detailLoading" class="detail-loading">
      <i class="el-icon-loading"></i>
      <span>加载中...</span>
    </div>

    <main class="content" v-else-if="detail">
      <div class="chips" v-if="topCategory || (analysisResult.label && analysisResult.label.length)">
        <span class="chip active" v-if="topCategory">{{ topCategory }}</span>
        <span class="chip" v-for="(item, idx) in analysisResult.label" :key="idx">#{{ item }}</span>
      </div>

      <h1 class="title">{{ analysisResult.theme || fallbackTitle }}</h1>
      <p class="time">
        <i class="el-icon-time"></i>
        {{ formatTime(displayCreatedTime) }}
      </p>

      <section class="summary-card">
        <h3>内容总结</h3>
        <div
          v-if="linesForDisplay(analysisResult.summary).length"
          ref="summaryContent"
          class="summary-content"
          @mouseup="onSummaryPointerUp"
          @touchend="onSummaryTouchEnd"
        >
          <div
            v-for="(line, i) in linesForDisplay(analysisResult.summary)"
            :key="'sum-' + i"
            class="summary-line"
          >
            {{ line }}
          </div>
        </div>
        <p v-else>暂无总结内容</p>
      </section>

      <section class="note-area">
        <div class="note-header-row">
          <div class="note-header-left">
            <div class="note-header">学习笔记</div>
            <span v-if="noteAiGenerating" class="note-ai-status">
              <span class="note-ai-status__text">智能小助手-Evy正在为您扩展知识</span>
              <span class="note-ai-status__dots" aria-hidden="true">
                <i>。</i><i>。</i><i>。</i>
              </span>
            </span>
          </div>
        </div>
        <div
          v-if="!isEditingNote"
          class="note-preview"
          :class="{ 'note-preview--empty': !noteContent.trim() }"
        >
          <div
            v-if="noteContent.trim()"
            class="note-preview__body"
            v-html="renderedNoteContent"
          />
          <span v-else class="note-preview__placeholder">记录你的学习收获...</span>
        </div>
        <textarea
          v-else
          v-model="noteDraft"
          maxlength="5000"
          placeholder="记录你的学习收获..."
        />
        <div class="note-action-row" :class="{ 'note-action-row--editing': isEditingNote }">
          <button
            v-if="isEditingNote"
            type="button"
            class="note-action-btn note-action-btn--cancel"
            :disabled="noteSaving || noteAiGenerating"
            @click="cancelEditNote"
          >
            取消
          </button>
          <button
            class="note-action-btn"
            :class="{ 'note-action-btn--save': isEditingNote }"
            :disabled="noteSaving || noteAiGenerating"
            @click="onNoteActionClick"
          >
            <template v-if="isEditingNote">
              {{ noteSaving ? '保存中...' : '保存笔记' }}
            </template>
            <template v-else>
              修改笔记
            </template>
          </button>
        </div>
      </section>
    </main>

    <div v-else-if="!detailLoading" class="empty">
      <p>暂无总结数据</p>
      <el-button type="primary" @click="$router.push('/summit')">去总结首页</el-button>
    </div>

    <DetailChatPanel
      ref="chatPanel"
      :summary-id="noteTargetId"
      :expand-disabled="noteSaving || !hasSummaryContent"
      :expand-loading="noteAiGenerating"
      @smart-expand="handleSmartSummarizeNote"
      @append-note="appendChatToNote"
    />

    <transition name="summary-ask-fade">
      <div
        v-if="summaryAskMenu.visible"
        ref="summaryAskPopover"
        class="summary-ask-popover"
        :style="summaryAskMenuStyle"
        @mousedown.stop
        @touchstart.stop
      >
        <button type="button" class="summary-ask-popover__btn" @click="askAboutSummarySelection">
          <i class="el-icon-chat-dot-round"></i>
          问一下
        </button>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from 'axios'
import DetailChatPanel from '@/components/DetailChatPanel.vue'
import { getApiErrorMessage } from '@/utils/apiErrorMessage'
import { splitNumberedLines } from '@/utils/splitNumberedText'
import { renderNoteContent } from '@/utils/renderNoteContent'

export default {
  name: 'SummitDetail',
  components: {
    DetailChatPanel
  },
  data() {
    return {
      detail: null,
      detailLoading: false,
      noteContent: '',
      noteDraft: '',
      isEditingNote: false,
      noteSaving: false,
      noteAiGenerating: false,
      userId: null,
      summaryAskMenu: {
        visible: false,
        text: '',
        left: 0,
        top: 0
      },
      summarySelectionTimer: null,
      isTouchDevice: false
    }
  },
  watch: {
    '$route.query.id'() {
      this.initDetailPage()
    }
  },
  computed: {
    fallbackTitle() {
      return this.detail?.sourceType === 'video' ? '新视频总结内容' : '新文本总结内容'
    },
    analysisResult() {
      const result = this.detail?.result || {}
      return {
        theme: result.theme || '',
        label: Array.isArray(result.label)
          ? result.label
          : Array.isArray(result.labels)
            ? result.labels
            : [],
        summary: result.summary || result.summarizedText || result.noteContent || '',
        parentLabel: result.parentLabel || '',
        parentLabelCode: result.parentLabelCode || ''
      }
    },
    topCategory() {
      return this.analysisResult.parentLabel || this.analysisResult.parentLabelCode || ''
    },
    displayCreatedTime() {
      return this.detail?.createdDate || this.detail?.createDate || this.detail?.createdAt || ''
    },
    noteTargetId() {
      const result = this.detail?.result || {}
      return result.idEzTextSummary || result.id || this.detail?.idEzTextSummary || ''
    },
    summaryAskMenuStyle() {
      return {
        left: `${this.summaryAskMenu.left}px`,
        top: `${this.summaryAskMenu.top}px`
      }
    },
    renderedNoteContent() {
      return renderNoteContent(this.noteContent)
    },
    summaryTextForAi() {
      const lines = this.linesForDisplay(this.analysisResult.summary)
      if (lines.length) return lines.join('\n')
      const raw = this.analysisResult.summary
      return raw == null ? '' : String(raw).trim()
    },
    hasSummaryContent() {
      return !!this.summaryTextForAi
    }
  },
  mounted() {
    this.isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0
    this.userId = localStorage.getItem('userId') || null
    this.initDetailPage()
    document.addEventListener('mousedown', this.onDocumentCloseSummaryAskMenu)
    document.addEventListener('touchend', this.onDocumentTouchEndClose, { passive: true })
    document.addEventListener('selectionchange', this.onSummarySelectionChange)
    window.addEventListener('scroll', this.hideSummaryAskMenu, true)
  },
  beforeDestroy() {
    document.removeEventListener('mousedown', this.onDocumentCloseSummaryAskMenu)
    document.removeEventListener('touchend', this.onDocumentTouchEndClose)
    document.removeEventListener('selectionchange', this.onSummarySelectionChange)
    window.removeEventListener('scroll', this.hideSummaryAskMenu, true)
    if (this.summarySelectionTimer) {
      window.clearTimeout(this.summarySelectionTimer)
      this.summarySelectionTimer = null
    }
  },
  methods: {
    readSessionPayload() {
      const raw = sessionStorage.getItem('summit-detail-payload')
      if (!raw) return null
      try {
        return JSON.parse(raw)
      } catch (e) {
        return null
      }
    },
    resolveRecordId() {
      const q = this.$route.query.id
      if (q != null && String(q).trim() !== '') {
        return String(q).trim()
      }
      const cached = this.readSessionPayload()
      const rid = cached?.result?.idEzTextSummary ?? cached?.result?.id
      if (rid != null && String(rid).trim() !== '') {
        return String(rid).trim()
      }
      return ''
    },
    mapDetailFromApi(data, recordId, cached) {
      const sourceType = cached?.sourceType || data.sourceType || 'text'
      const labels = Array.isArray(data.labels)
        ? data.labels
        : data.labels != null && data.labels !== ''
          ? [String(data.labels)]
          : []
      const cachedLabels = cached?.result?.label
      const labelFinal = labels.length
        ? labels
        : Array.isArray(cachedLabels)
          ? cachedLabels
          : []

      const theme = data.theme || cached?.result?.theme || ''
      const summary =
        data.summarized_text ||
        data.summarizedText ||
        data.content ||
        data.summary ||
        cached?.result?.summary ||
        ''
      const noteContent =
        data.noteContent ??
        data.note ??
        data.remark ??
        data.memo ??
        cached?.result?.noteContent ??
        ''

      const createdDate =
        data.createDate ||
        data.createdDate ||
        data.createTime ||
        data.createdAt ||
        data.create_date ||
        cached?.createdDate ||
        ''
      const createdAt = cached?.createdAt || data.createdAt || createdDate || Date.now()

      return {
        sourceType,
        createdDate,
        createdAt,
        result: {
          idEzTextSummary: String(recordId),
          id: String(recordId),
          theme,
          label: labelFinal,
          summary,
          noteContent,
          parentLabel: data.parentLabel || cached?.result?.parentLabel || '',
          parentLabelCode: data.parentLabelCode || cached?.result?.parentLabelCode || ''
        }
      }
    },
    scrollDetailToTop() {
      this.$nextTick(() => {
        const contentArea = document.querySelector('.content-area')
        if (contentArea && typeof contentArea.scrollTo === 'function') {
          contentArea.scrollTo({ top: 0, behavior: 'auto' })
        }
        if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
          window.scrollTo({ top: 0, behavior: 'auto' })
        }
      })
    },
    async initDetailPage() {
      this.detail = null
      this.isEditingNote = false
      const recordId = this.resolveRecordId()
      if (!recordId) {
        this.detailLoading = false
        return
      }
      this.userId = localStorage.getItem('userId') || null
      if (!this.userId) {
        this.$message.error('请先登录')
        this.detailLoading = false
        return
      }

      this.detailLoading = true
      const cached = this.readSessionPayload()
      try {
        const res = await axios.post('/api/app/core/text-summary/detail', {
          id: recordId
        })
        const data = res?.data ?? {}
        this.detail = this.mapDetailFromApi(data, recordId, cached)
        this.noteContent = this.detail?.result?.noteContent || ''
        this.noteDraft = this.noteContent
        this.scrollDetailToTop()
      } catch (error) {
        console.error('获取详情失败:', error)
        this.$message.error(getApiErrorMessage(error, '获取详情失败，请重试'))
        this.detail = null
      } finally {
        this.detailLoading = false
      }
    },
    linesForDisplay(text) {
      return splitNumberedLines(text)
    },
    scheduleSummaryAskMenuUpdate(delay) {
      if (this.summarySelectionTimer) {
        window.clearTimeout(this.summarySelectionTimer)
      }
      this.summarySelectionTimer = window.setTimeout(() => {
        this.summarySelectionTimer = null
        this.updateSummaryAskMenu()
      }, delay)
    },
    onSummarySelectionChange() {
      const delay = this.isTouchDevice ? 280 : 80
      this.scheduleSummaryAskMenuUpdate(delay)
    },
    onSummaryPointerUp() {
      if (this.isTouchDevice) return
      this.scheduleSummaryAskMenuUpdate(80)
    },
    onSummaryTouchEnd() {
      if (!this.isTouchDevice) return
      // 移动端选区在 touchend 后才稳定，延迟检测并二次兜底
      this.scheduleSummaryAskMenuUpdate(120)
      window.setTimeout(() => {
        if (!this.summaryAskMenu.visible) {
          this.updateSummaryAskMenu()
        }
      }, 420)
    },
    updateSummaryAskMenu() {
      const summaryEl = this.$refs.summaryContent
      if (!summaryEl) {
        this.hideSummaryAskMenu()
        return
      }
      const selection = window.getSelection()
      if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
        this.hideSummaryAskMenu()
        return
      }
      const text = selection.toString().replace(/\s+/g, ' ').trim()
      if (!text) {
        this.hideSummaryAskMenu()
        return
      }
      if (!this.isSelectionInSummary(selection, summaryEl)) {
        this.hideSummaryAskMenu()
        return
      }
      const range = selection.getRangeAt(0)
      const rect = range.getBoundingClientRect()
      if (!rect.width && !rect.height) {
        this.hideSummaryAskMenu()
        return
      }
      this.showSummaryAskMenu(text, rect)
    },
    isSelectionInSummary(selection, summaryEl) {
      const { anchorNode, focusNode } = selection
      if (this.isNodeInSummary(anchorNode, summaryEl) && this.isNodeInSummary(focusNode, summaryEl)) {
        return true
      }
      try {
        const range = selection.getRangeAt(0)
        return summaryEl.contains(range.commonAncestorContainer)
      } catch (e) {
        return false
      }
    },
    isNodeInSummary(node, summaryEl) {
      if (!node) return false
      const el = node.nodeType === Node.TEXT_NODE ? node.parentNode : node
      return !!(el && summaryEl.contains(el))
    },
    showSummaryAskMenu(text, rect) {
      const menuWidth = 108
      const menuHeight = 32
      const gap = 10
      let left = rect.left + rect.width / 2 - menuWidth / 2
      left = Math.max(12, Math.min(left, window.innerWidth - menuWidth - 12))
      // 默认显示在选区下方
      let top = rect.bottom + gap
      if (top + menuHeight > window.innerHeight - 12) {
        top = Math.max(12, rect.top - menuHeight - gap)
      }
      this.summaryAskMenu = {
        visible: true,
        text,
        left,
        top
      }
    },
    hideSummaryAskMenu() {
      this.summaryAskMenu.visible = false
      this.summaryAskMenu.text = ''
    },
    onDocumentCloseSummaryAskMenu(e) {
      if (!this.summaryAskMenu.visible) return
      const popover = this.$refs.summaryAskPopover
      if (popover && popover.contains(e.target)) return
      const summaryEl = this.$refs.summaryContent
      if (summaryEl && summaryEl.contains(e.target)) {
        const selection = window.getSelection()
        if (selection && !selection.isCollapsed && this.isSelectionInSummary(selection, summaryEl)) {
          return
        }
      }
      this.hideSummaryAskMenu()
    },
    onDocumentTouchEndClose(e) {
      if (!this.isTouchDevice || !this.summaryAskMenu.visible) return
      window.setTimeout(() => {
        this.onDocumentCloseSummaryAskMenu(e)
      }, 60)
    },
    askAboutSummarySelection() {
      const text = (this.summaryAskMenu.text || '').trim()
      this.hideSummaryAskMenu()
      this.clearSummarySelection()
      if (!text) return
      const chatPanel = this.$refs.chatPanel
      if (chatPanel && typeof chatPanel.openWithSelectedText === 'function') {
        chatPanel.openWithSelectedText(text)
      }
    },
    clearSummarySelection() {
      const selection = window.getSelection()
      if (selection && selection.rangeCount > 0) {
        selection.removeAllRanges()
      }
    },
    goBack() {
      const from = this.$route.query.from
      if (from === 'profile-tasks') {
        if (window.history.length > 1) {
          this.$router.back()
        } else {
          this.$router.replace({ path: '/profile/tasks' })
        }
        return
      }
      if (from === 'my-notes') {
        this.$router.push({ path: '/summary-list/notes' })
        return
      }
      if (from === 'note-cards') {
        this.$router.push({ path: '/note-cards' })
        return
      }
      if (this.$route.path === '/summary-list/detail') {
        this.$router.push({
          path: '/summary-list',
          query: { restoreScroll: String(Date.now()) }
        })
        return
      }
      this.$router.back()
    },
    toggleEdit() {
      if (this.isEditingNote) {
        this.cancelEditNote()
        return
      }
      this.noteDraft = this.noteContent
      this.isEditingNote = true
    },
    cancelEditNote() {
      if (this.noteSaving) return
      this.noteDraft = this.noteContent
      this.isEditingNote = false
    },
    onNoteActionClick() {
      if (this.isEditingNote) {
        this.handleSaveNote()
        return
      }
      this.toggleEdit()
    },
    async handleSmartSummarizeNote() {
      const summaryText = this.summaryTextForAi
      if (!summaryText) {
        this.$message.warning('暂无总结内容，无法生成笔记')
        return
      }
      if (this.noteAiGenerating) return

      this.noteAiGenerating = true
      try {
        const res = await axios.post('/api/app/ai/learningNote', {
          text: summaryText
        })
        const noteText = typeof res.data === 'string'
          ? res.data
          : (res.data?.noteContent || res.data?.text || '')
        const trimmed = String(noteText || '').trim()
        if (!trimmed) {
          this.$message.warning('AI 未返回有效笔记内容，请重试')
          return
        }
        this.appendChatToNote(trimmed)
      } catch (error) {
        this.$message.error(getApiErrorMessage(error, '智能知识扩展失败，请重试'))
      } finally {
        this.noteAiGenerating = false
      }
    },
    appendChatToNote(text) {
      const snippet = String(text || '').trim()
      if (!snippet) return

      if (!this.isEditingNote) {
        this.noteDraft = this.noteContent
        this.isEditingNote = true
      }

      const separator = this.noteDraft.trim() ? '\n\n' : ''
      const next = `${this.noteDraft}${separator}${snippet}`
      if (next.length > 5000) {
        this.$message.warning('笔记内容不能超过 5000 字，已截断超出部分')
        this.noteDraft = next.slice(0, 5000)
      } else {
        this.noteDraft = next
      }

      this.$nextTick(() => {
        const noteTextarea = this.$el && this.$el.querySelector('.note-area textarea')
        if (noteTextarea && typeof noteTextarea.scrollIntoView === 'function') {
          noteTextarea.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      })
      this.$message.success('已追加到学习笔记，记得保存')
    },
    async handleSaveNote() {
      const noteContent = this.noteDraft.trim()
      if (!noteContent) {
        this.$message.warning('请输入笔记内容')
        return
      }
      if (!this.userId) {
        this.$message.error('请先登录')
        return
      }
      if (!this.noteTargetId) {
        this.$message.error('未获取到总结ID，无法保存笔记')
        return
      }
      if (this.noteSaving) return

      this.noteSaving = true
      try {
        const res = await axios.post('/api/app/core/note/add', {
          idEzTextSummary: this.noteTargetId,
          noteContent
        })
        const savedContent = res?.data?.noteContent ?? noteContent
        this.noteContent = savedContent
        this.noteDraft = savedContent
        if (this.detail && this.detail.result) {
          this.detail.result.noteContent = savedContent
        }
        this.isEditingNote = false
        this.$message.success('笔记保存成功')
      } catch (error) {
        this.$message.error(getApiErrorMessage(error, '笔记保存失败，请重试'))
      } finally {
        this.noteSaving = false
      }
    },
    formatTime(timestamp) {
      if (!timestamp) return ''
      const date = new Date(timestamp)
      const y = date.getFullYear()
      const m = `${date.getMonth() + 1}`.padStart(2, '0')
      const d = `${date.getDate()}`.padStart(2, '0')
      const hh = `${date.getHours()}`.padStart(2, '0')
      const mm = `${date.getMinutes()}`.padStart(2, '0')
      const ss = `${date.getSeconds()}`.padStart(2, '0')
      return `${y}/${m}/${d} ${hh}:${mm}:${ss}`
    }
  }
}
</script>

<style scoped>
.summit-detail-page {
  min-height: 100vh;
  background: #ffffff;
}

.top-bar {
  height: 58px;
  border-bottom: 1px solid #f1f4fb;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 10px;
}

.top-bar h2 {
  margin: 0;
  font-size: clamp(18px, 2.2vw, 22px);
  color: #16294f;
}

.icon-btn {
  border: none;
  background: transparent;
  width: 40px;
  height: 40px;
  color: #586a8f;
  font-size: 18px;
}

.header-placeholder {
  width: 40px;
  height: 40px;
}

.content {
  padding: 16px 14px 24px;
}

.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.chip {
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 10px;
  border-radius: 12px;
  background: #f3f6fd;
  color: #5d6f94;
  font-size: 11px;
}

.chip.active {
  background: #4652f5;
  color: #fff;
}

.title {
  margin: 14px 0 8px;
  font-size: clamp(24px, 3.2vw, 32px);
  color: #14284d;
  font-weight: 700;
}

.time {
  margin: 0;
  color: #97a5c2;
  font-size: 13px;
}

.time i {
  margin-right: 6px;
}

.summary-card {
  margin-top: 18px;
  border: 1px solid #f0f4fb;
  border-radius: 16px;
  background: #fafcff;
  padding: 14px;
}

.summary-card h3 {
  margin: 0;
  color: #97a5c2;
  font-size: 14px;
}

.summary-card p {
  margin: 12px 0;
  color: #384c75;
  font-size: clamp(16px, 2.2vw, 22px);
  line-height: 1.5;
}

.summary-content {
  margin: 12px 0;
  user-select: text;
  -webkit-user-select: text;
  touch-action: auto;
}

.summary-line {
  color: #384c75;
  font-size: clamp(16px, 2.2vw, 22px);
  line-height: 1.5;
}

.summary-line + .summary-line {
  margin-top: 10px;
}

.summary-ask-popover {
  position: fixed;
  z-index: 115;
  padding: 0;
  border: none;
  background: transparent;
  box-shadow: none;
}

.summary-ask-popover__btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 12px;
  border: none;
  border-radius: 10px;
  background: linear-gradient(135deg, #4253ff 0%, #3546e8 100%);
  color: #ffffff;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  box-shadow: 0 6px 18px rgba(66, 83, 255, 0.32);
  -webkit-tap-highlight-color: transparent;
}

.summary-ask-popover__btn i {
  font-size: 15px;
}

.summary-ask-fade-enter-active,
.summary-ask-fade-leave-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.summary-ask-fade-enter,
.summary-ask-fade-leave-to {
  opacity: 0;
  transform: translateY(4px) scale(0.96);
}

.note-area {
  margin-top: 20px;
}

.note-header-row {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 12px;
  margin-bottom: 10px;
}

.note-header-left {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 8px;
  min-width: 0;
}

.note-header {
  color: #0a1c44;
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 0;
  flex-shrink: 0;
}

.note-ai-status {
  display: inline-flex;
  align-items: baseline;
  gap: 1px;
  color: #6878ff;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.3;
  white-space: nowrap;
  animation: noteAiStatusPulse 1.6s ease-in-out infinite;
}

.note-ai-status__text {
  background: linear-gradient(
    90deg,
    #6878ff 0%,
    #4253ff 35%,
    #9aa6ff 50%,
    #4253ff 65%,
    #6878ff 100%
  );
  background-size: 200% 100%;
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  -webkit-text-fill-color: transparent;
  animation: noteAiStatusShimmer 2.2s linear infinite;
}

.note-ai-status__dots {
  display: inline-flex;
  align-items: flex-end;
  height: 1em;
  margin-left: 1px;
  color: #6878ff;
  -webkit-text-fill-color: #6878ff;
  font-weight: 700;
  letter-spacing: 0.5px;
}

.note-ai-status__dots i {
  font-style: normal;
  display: inline-block;
  animation: noteAiStatusBounce 0.9s ease-in-out infinite;
}

.note-ai-status__dots i:nth-child(2) {
  animation-delay: 0.15s;
}

.note-ai-status__dots i:nth-child(3) {
  animation-delay: 0.3s;
}

@keyframes noteAiStatusPulse {
  0%,
  100% {
    opacity: 0.72;
  }
  50% {
    opacity: 1;
  }
}

@keyframes noteAiStatusShimmer {
  0% {
    background-position: 100% 0;
  }
  100% {
    background-position: -100% 0;
  }
}

@keyframes noteAiStatusBounce {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-3px);
  }
}

@media (prefers-reduced-motion: reduce) {
  .note-ai-status,
  .note-ai-status__text,
  .note-ai-status__dots i {
    animation: none;
  }

  .note-ai-status {
    opacity: 1;
  }

  .note-ai-status__text {
    color: #6878ff;
    -webkit-text-fill-color: #6878ff;
    background: none;
  }
}

.note-action-row {
  margin-top: 12px;
}

.note-action-row--editing {
  display: flex;
  gap: 10px;
}

.note-action-btn {
  width: 100%;
  height: 42px;
  border: none;
  border-radius: 10px;
  background: linear-gradient(135deg, #4253ff 0%, #3546e8 100%);
  color: #ffffff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  box-sizing: border-box;
  transition: opacity 0.2s ease, box-shadow 0.2s ease;
  -webkit-tap-highlight-color: transparent;
}

.note-action-row--editing .note-action-btn {
  flex: 1;
}

.note-action-btn:hover {
  box-shadow: 0 4px 12px rgba(66, 83, 255, 0.32);
}

.note-action-btn:active {
  opacity: 0.92;
}

.note-action-btn--cancel {
  background: #eef1f8;
  color: #3a4a6d;
  border: 1px solid #c5cee3;
}

.note-action-btn--cancel:hover {
  box-shadow: 0 4px 12px rgba(15, 28, 68, 0.1);
  border-color: #aeb9d4;
  background: #e6ebf5;
  color: #2a3a5c;
}

.note-action-btn--save {
  background: linear-gradient(135deg, #1f2b50, #0f172f);
}

.note-action-btn--save:hover {
  box-shadow: 0 4px 12px rgba(15, 23, 47, 0.28);
}

.note-action-btn:disabled {
  opacity: 0.72;
  cursor: not-allowed;
  box-shadow: none;
}

.note-area textarea {
  width: 100%;
  min-height: 160px;
  border: none;
  background: #fff9e8;
  border-radius: 14px;
  padding: 12px;
  box-sizing: border-box;
  color: #6f5932;
  font-size: clamp(14px, 2vw, 16px);
  resize: vertical;
}

.note-preview {
  width: 100%;
  min-height: 160px;
  border: none;
  background: #fffef6;
  border-radius: 14px;
  padding: 12px;
  box-sizing: border-box;
  font-size: clamp(14px, 2vw, 16px);
}

.note-preview--empty {
  min-height: 160px;
}

.note-preview__placeholder {
  color: #c4b89a;
  line-height: 1.5;
}

.note-preview__body {
  color: #6f5932;
  line-height: 1.6;
  word-break: break-word;
}

.note-preview__body >>> .note-plain-text {
  margin: 0;
  color: #8a7756;
  line-height: 1.6;
}

.note-preview__body >>> p {
  margin: 0 0 10px;
}

.note-preview__body >>> p:last-child {
  margin-bottom: 0;
}

.note-preview__body >>> h1,
.note-preview__body >>> h2,
.note-preview__body >>> h3,
.note-preview__body >>> h4,
.note-preview__body >>> h5,
.note-preview__body >>> h6 {
  margin: 0 0 8px;
  color: #5a4a2e;
  font-weight: 700;
  line-height: 1.4;
}

.note-preview__body >>> h1 { font-size: 1.35em; }
.note-preview__body >>> h2 { font-size: 1.2em; }
.note-preview__body >>> h3 { font-size: 1.1em; }

.note-preview__body >>> ul,
.note-preview__body >>> ol {
  margin: 0 0 10px;
  padding-left: 1.4em;
}

.note-preview__body >>> li {
  margin-bottom: 4px;
}

.note-preview__body >>> li:last-child {
  margin-bottom: 0;
}

.note-preview__body >>> blockquote {
  margin: 0 0 10px;
  padding: 8px 12px;
  border-left: 3px solid #e8d9b0;
  background: rgba(255, 255, 255, 0.55);
  color: #7a6848;
}

.note-preview__body >>> code {
  padding: 1px 5px;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.75);
  font-family: Consolas, Monaco, 'Courier New', monospace;
  font-size: 0.92em;
}

.note-preview__body >>> pre {
  margin: 0 0 10px;
  padding: 10px 12px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.75);
  overflow-x: auto;
}

.note-preview__body >>> pre code {
  padding: 0;
  background: transparent;
}

.note-preview__body >>> a {
  color: #4253ff;
  text-decoration: underline;
}

.note-preview__body >>> hr {
  margin: 10px 0;
  border: none;
  border-top: 1px solid #eadfc4;
}

.note-preview__body >>> table {
  width: 100%;
  margin: 0 0 10px;
  border-collapse: collapse;
  font-size: 0.95em;
}

.note-preview__body >>> th,
.note-preview__body >>> td {
  border: 1px solid #eadfc4;
  padding: 6px 8px;
  text-align: left;
}

.note-preview__body >>> th {
  background: rgba(255, 255, 255, 0.65);
}

.note-area textarea:focus {
  outline: none;
  box-shadow: none;
}

.detail-loading {
  min-height: 50vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #5e73a8;
  font-size: 15px;
}

.detail-loading .el-icon-loading {
  font-size: 28px;
  animation: rotating 1.2s linear infinite;
}

@keyframes rotating {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.empty {
  min-height: 60vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #909cb3;
  font-size: 14px;
}

@media (max-width: 768px) {
  .top-bar {
    height: 52px;
  }

  .content {
    padding: 14px 12px 20px;
  }

  .title {
    margin-top: 12px;
    font-size: 24px;
  }

  .summary-card p {
    font-size: 16px;
  }
}
</style>
